import { inject, Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { TokenResponseDto } from 'api-client';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { LogService } from 'shared';

export interface TokenPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

const VAULT_KEYS = {
  ACCESS_TOKEN: 'vault_access_token',
  REFRESH_TOKEN: 'vault_refresh_token',
  TOKEN_METADATA: 'vault_token_metadata',
  PIN_HASH: 'vault_pin_hash',
  PIN_SALT: 'vault_pin_salt',
  BIOMETRIC_ENABLED: 'vault_biometric_enabled',
} as const;

// Legacy keys in @capacitor/preferences (for migration)
const LEGACY_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_METADATA: 'token_metadata',
  BIOMETRIC_ENABLED: 'biometric_enabled',
} as const;

// Lockout stored in Preferences (rate-limit, not secret — survives vault wipe)
const LOCKOUT_KEYS = {
  ATTEMPT_COUNT: 'lockout_attempt_count',
  LOCKOUT_EXPIRY: 'lockout_expiry',
} as const;

@Injectable({
  providedIn: 'root',
})
export class CredentialVaultService {
  private readonly logger = inject(LogService);
  private migrationDone = false;

  // ── Token Operations ──

  async saveToken(token: TokenResponseDto): Promise<void> {
    if (!token.accessToken) {
      this.logger.error('Invalid token format: no accessToken', 'CredentialVault.saveToken');
      return;
    }

    await this.secureSet(VAULT_KEYS.ACCESS_TOKEN, token.accessToken);

    if (token.refreshToken) {
      await this.secureSet(VAULT_KEYS.REFRESH_TOKEN, token.refreshToken);
    }

    interface TokenWithTwoFactor extends TokenResponseDto {
      requiresTwoFactor?: boolean;
    }
    const requiresTwoFactor = (token as TokenWithTwoFactor).requiresTwoFactor;

    await this.secureSet(
      VAULT_KEYS.TOKEN_METADATA,
      JSON.stringify({
        ...(typeof requiresTwoFactor !== 'undefined' && { requiresTwoFactor }),
      }),
    );
  }

  async getToken(): Promise<TokenResponseDto | null> {
    await this.ensureMigration();

    const accessToken = await this.secureGet(VAULT_KEYS.ACCESS_TOKEN);
    if (!accessToken) return null;

    const refreshToken = await this.secureGet(VAULT_KEYS.REFRESH_TOKEN);
    const metadataJson = await this.secureGet(VAULT_KEYS.TOKEN_METADATA);

    let requiresTwoFactor: boolean | undefined = undefined;
    if (metadataJson) {
      try {
        const metadata = JSON.parse(metadataJson) as { requiresTwoFactor?: boolean };
        requiresTwoFactor = metadata.requiresTwoFactor;
      } catch {
        this.logger.error('Failed to parse token metadata', 'CredentialVault.getToken');
      }
    }

    return {
      accessToken,
      refreshToken: refreshToken ?? '',
      ...(typeof requiresTwoFactor !== 'undefined' && { requiresTwoFactor }),
    };
  }

  async clearTokens(): Promise<void> {
    await this.secureRemove(VAULT_KEYS.ACCESS_TOKEN);
    await this.secureRemove(VAULT_KEYS.REFRESH_TOKEN);
    await this.secureRemove(VAULT_KEYS.TOKEN_METADATA);
  }

  // ── PIN Operations ──

  async savePinHash(hash: string, salt: string): Promise<void> {
    await this.secureSet(VAULT_KEYS.PIN_HASH, hash);
    await this.secureSet(VAULT_KEYS.PIN_SALT, salt);

    // Read back to verify
    const readBackHash = await this.secureGet(VAULT_KEYS.PIN_HASH);
    if (readBackHash !== hash) {
      throw new Error('PIN hash write verification failed');
    }
  }

  async getPinHash(): Promise<{ hash: string; salt: string } | null> {
    const hash = await this.secureGet(VAULT_KEYS.PIN_HASH);
    const salt = await this.secureGet(VAULT_KEYS.PIN_SALT);
    if (!hash || !salt) return null;
    return { hash, salt };
  }

  async clearPinHash(): Promise<void> {
    await this.secureRemove(VAULT_KEYS.PIN_HASH);
    await this.secureRemove(VAULT_KEYS.PIN_SALT);
  }

  async isPinConfigured(): Promise<boolean> {
    const pinData = await this.getPinHash();
    return pinData !== null;
  }

  // ── Biometric Flag ──

  async setBiometricEnabled(enabled: boolean): Promise<void> {
    await this.secureSet(VAULT_KEYS.BIOMETRIC_ENABLED, String(enabled));
  }

  async isBiometricEnabled(): Promise<boolean> {
    const value = await this.secureGet(VAULT_KEYS.BIOMETRIC_ENABLED);
    return value === 'true';
  }

  // ── Lockout State (Preferences — survives vault wipe) ──

  async getLockoutState(): Promise<{ attemptCount: number; lockoutExpiry: number }> {
    const { value: countStr } = await Preferences.get({ key: LOCKOUT_KEYS.ATTEMPT_COUNT });
    const { value: expiryStr } = await Preferences.get({ key: LOCKOUT_KEYS.LOCKOUT_EXPIRY });
    return {
      attemptCount: countStr ? parseInt(countStr, 10) : 0,
      lockoutExpiry: expiryStr ? parseInt(expiryStr, 10) : 0,
    };
  }

  async setLockoutState(attemptCount: number, lockoutExpiry: number): Promise<void> {
    await Preferences.set({
      key: LOCKOUT_KEYS.ATTEMPT_COUNT,
      value: String(attemptCount),
    });
    await Preferences.set({
      key: LOCKOUT_KEYS.LOCKOUT_EXPIRY,
      value: String(lockoutExpiry),
    });
  }

  async clearLockoutState(): Promise<void> {
    await Preferences.remove({ key: LOCKOUT_KEYS.ATTEMPT_COUNT });
    await Preferences.remove({ key: LOCKOUT_KEYS.LOCKOUT_EXPIRY });
  }

  // ── Token Utilities ──

  decodeToken(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload) as TokenPayload;
    } catch {
      return null;
    }
  }

  async isAccessTokenExpired(): Promise<boolean> {
    const token = await this.getToken();
    if (!token?.accessToken) return true;
    const payload = this.decodeToken(token.accessToken);
    if (!payload?.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }

  // ── Clear All (logout) ──

  async clearAll(): Promise<void> {
    await this.clearTokens();
    await this.clearPinHash();
    await this.secureRemove(VAULT_KEYS.BIOMETRIC_ENABLED);
    // Note: lockout state intentionally NOT cleared on logout (per plan item #6)
  }

  // ── Migration: Preferences → SecureStorage ──

  async ensureMigration(): Promise<void> {
    if (this.migrationDone) return;
    this.migrationDone = true;

    try {
      // Check if legacy tokens exist in Preferences
      const { value: legacyAccessToken } = await Preferences.get({
        key: LEGACY_KEYS.ACCESS_TOKEN,
      });

      if (!legacyAccessToken) return; // No migration needed

      this.logger.info('Migrating tokens from Preferences to SecureStorage', 'CredentialVault');

      // Migrate access token
      await this.secureSet(VAULT_KEYS.ACCESS_TOKEN, legacyAccessToken);

      // Verify write
      const readBack = await this.secureGet(VAULT_KEYS.ACCESS_TOKEN);
      if (readBack !== legacyAccessToken) {
        this.logger.error('Migration write verification failed', 'CredentialVault');
        this.migrationDone = false; // Retry next time
        return;
      }

      // Migrate refresh token
      const { value: legacyRefreshToken } = await Preferences.get({
        key: LEGACY_KEYS.REFRESH_TOKEN,
      });
      if (legacyRefreshToken) {
        await this.secureSet(VAULT_KEYS.REFRESH_TOKEN, legacyRefreshToken);
      }

      // Migrate metadata
      const { value: legacyMetadata } = await Preferences.get({
        key: LEGACY_KEYS.TOKEN_METADATA,
      });
      if (legacyMetadata) {
        await this.secureSet(VAULT_KEYS.TOKEN_METADATA, legacyMetadata);
      }

      // Migrate biometric flag
      const { value: legacyBiometric } = await Preferences.get({
        key: LEGACY_KEYS.BIOMETRIC_ENABLED,
      });
      if (legacyBiometric) {
        await this.secureSet(VAULT_KEYS.BIOMETRIC_ENABLED, legacyBiometric);
      }

      // Delete legacy keys only after successful migration
      await Preferences.remove({ key: LEGACY_KEYS.ACCESS_TOKEN });
      await Preferences.remove({ key: LEGACY_KEYS.REFRESH_TOKEN });
      await Preferences.remove({ key: LEGACY_KEYS.TOKEN_METADATA });
      await Preferences.remove({ key: LEGACY_KEYS.BIOMETRIC_ENABLED });

      this.logger.info('Migration complete', 'CredentialVault');
    } catch (error) {
      this.logger.error(
        'Migration failed — will use Preferences fallback',
        'CredentialVault',
        error,
      );
      this.migrationDone = false; // Retry next time
    }
  }

  // ── SecureStorage Wrapper (with iOS first-unlock handling) ──

  private async secureSet(key: string, value: string): Promise<void> {
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch (error) {
      // iOS: SecureStorage unavailable before device first-unlock
      if (this.isFirstUnlockError(error)) {
        this.logger.warn(
          'SecureStorage unavailable before device unlock — retrying',
          'CredentialVault',
        );
        await this.delay(500);
        await SecureStoragePlugin.set({ key, value });
      } else {
        throw error;
      }
    }
  }

  private async secureGet(key: string): Promise<string | null> {
    try {
      const result = await SecureStoragePlugin.get({ key });
      return result.value ?? null;
    } catch (error) {
      const errorMsg = ((error as Error).message ?? String(error)).toLowerCase();
      if (errorMsg.includes('not found') || errorMsg.includes('does not exist')) {
        return null;
      }

      // iOS first-unlock: retry once
      if (this.isFirstUnlockError(error)) {
        this.logger.warn('SecureStorage unavailable — retrying after delay', 'CredentialVault');
        await this.delay(500);
        try {
          const result = await SecureStoragePlugin.get({ key });
          return result.value ?? null;
        } catch {
          return null;
        }
      }

      this.logger.error(`SecureStorage get failed for ${key}`, 'CredentialVault', error);
      return null;
    }
  }

  private async secureRemove(key: string): Promise<void> {
    try {
      await SecureStoragePlugin.remove({ key });
    } catch (error) {
      const errorMsg = ((error as Error).message ?? String(error)).toLowerCase();
      if (!errorMsg.includes('not found') && !errorMsg.includes('does not exist')) {
        this.logger.error(`SecureStorage remove failed for ${key}`, 'CredentialVault', error);
      }
    }
  }

  private isFirstUnlockError(error: unknown): boolean {
    const msg = ((error as Error).message ?? String(error)).toLowerCase();
    return msg.includes('user not authenticated') || msg.includes('errSecInteractionNotAllowed');
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
